/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { useCallback, useState } from "react";
import { useBooking } from "./useBooking";
import { useValidateTime } from "../lib/react-query/useValidateTime";
import { useToast } from "../components/ui/Toast";

export const useTimeSlotSelection = () => {
  const [pendingTime, setPendingTime] = useState(null);
  const {
    selectedServices,
    selectedCategories,
    bookingRequest,
    setSelectedTime,
    setSelectedDate,
    updateBooking,
    selectedTime,
    selectedDate,
    selectedProvider,
  } = useBooking();

  const { toast } = useToast();
  const { mutateAsync: validateTime, isPending } = useValidateTime();

  const showErrorToast = useCallback(
    (errorMessage) => {
      toast({
        title: "Error!",
        description: errorMessage,
        variant: "error",
      });
    },
    [toast]
  );

  const validateTimeSlot = useCallback(
    async (date, time, services, categories) => {
      if (!time || !date) return;

      const validationServices = services || selectedServices;
      const validationCategories = categories || selectedCategories;

      const updatedRequest = {
        ...bookingRequest,
        start_datetime: `${date} ${time}`,
        service_id:
          validationServices.length === 1 && validationServices[0].recurring_settings
            ? validationServices[0].id
            : validationServices.map((service) => service.id),
        category_id:
          validationCategories.length === 1
            ? validationCategories[0].id
            : validationCategories.map((category) => category.id),
        count: [validationServices[0].group_booking_count || 1],
      };

      const res = await validateTime(updatedRequest);
      const hasRecurringServices = validationServices?.some((service) => !!service.recurring_settings);
      const isRecurring = hasRecurringServices && res.dates?.length > 1;

      if (res.error) {
        showErrorToast(res.error);
        setSelectedTime(null);
        return false;
      }

      if (!res.error) {
        updateBooking({ ...updatedRequest, recurring: isRecurring ? res : null });
      }

      return true;
    },
    [selectedServices, selectedCategories, bookingRequest, validateTime, showErrorToast, setSelectedTime, updateBooking]
  );

  const selectTimeSlot = async (date, time, provider) => {
    if (selectedTime === time && selectedDate === date && selectedProvider.id === provider.id) {
      return;
    }

    setPendingTime(time);
    try {
      const isValid = await validateTimeSlot(date, time);

      if (isValid) {
        setSelectedTime(time);
        setSelectedDate(date);
      }
    } finally {
      setPendingTime(null);
    }
  };

  return {
    isPending,
    pendingTime,
    selectTimeSlot,
    validateTimeSlot,
  };
};
